import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

const Portal = ({
  children,
  className,
  ref,
  style,
  inert,
  ...rest
}: React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>) => {
  const [container] = useState(() => {
    const element = document.createElement('div');
    Object.assign(element, { className, style, inert, ...rest });
    // add data-testid to the element
    element.setAttribute('data-testid', 'portal');
    return element;
  });

  // ensure container gets the className prop
  // this is necessary because the container won't re-render with the className prop
  // so we need to manually set it whenever the className prop changes
  useEffect(() => {
    if (container) {
      container.className = className || '';
      container.inert = !!inert;
      container.style.cssText = style
        ? Object.entries(style)
            .map(([key, value]) => `${key}: ${value};`)
            .join(' ')
        : '';
    }
    // only pass in props you want to make the element re-render with
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [className, inert, style]);

  useEffect(() => {
    document.body.appendChild(container);

    // add ref
    if (ref) {
      if (typeof ref === 'function') {
        ref(container);
      } else {
        ref.current = container;
      }
    }

    return () => {
      document.body.removeChild(container);
    };
  }, [container, ref]);

  return ReactDOM.createPortal(children, container);
};

export default Portal;
